import { QuestionCircleOutlined } from '@ant-design/icons'
import { Col, Form, Input, InputNumber, Modal, Row, Select, Switch, Tooltip } from 'antd'
import { IBaseFormProps } from 'Modules'
import { useMemo } from 'react'
import { isAddress } from 'viem'

import DatePicker from 'component/date-picker/date-picker'
import MarkdownEditor from 'component/markdown-editor/markdown-editor'
import SelectCountry from 'component/select/select-country'
import { PTO_TYPE_OPTIONS, PTOTypeEnum } from 'constants/pto'
import { MarketPlaceProvider } from 'context/marketplace/market-place-provider'
import { convertNumberToDateTime } from 'utils/datetime'

import ButtonEnableMarketPlace from './button-enable-market-place'
import ButtonSendGift from './button-send-gift'
import ButtonUploadWhitelist from './button-upload-whitelist'

interface IBaseInformationProps extends IBaseFormProps {
  id?: string | null | undefined
  switchChecked?: boolean
  setSwitchChecked?: (e: boolean) => void
}

const BaseInformation = ({
  id,
  mode = 'edit',
  edit = true,
  form,
  switchChecked = false,
  setSwitchChecked,
}: IBaseInformationProps) => {
  const isModeCreate = useMemo(() => {
    return mode === 'create'
  }, [mode])

  const handleSwitchClick = () => {
    Modal.confirm({
      title: 'Are you sure you want to change the status?',
      content:
        'Toggle the switch to manage your PTO status. When the switch is ON, PTO is active. When the switch is OFF, PTO is inactive.',
      onOk: () => {
        setSwitchChecked?.(!switchChecked) // Toggle the switch state
      },
      okText: 'Yes',
      cancelText: 'No',
      centered: true,
    })
  }

  return (
    <fieldset>
      <legend>Basic Information</legend>
      <Row gutter={24}>
        {/* Name */}
        <Col span={12}>
          <Form.Item
            label={
              <>
                Name
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO name, please enter your chosen name as text
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="name"
            rules={[
              { required: true, message: 'Please input the name!' },
              {
                min: 3,
                max: 50,
                message: 'Name must be between 3 and 50 characters!',
              },
            ]}
          >
            <Input
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              disabled={!edit}
              placeholder="Enter the name"
            />
          </Form.Item>
        </Col>

        {/* Total Supply */}
        <Col span={12}>
          <Form.Item
            label={
              <>
                Total Supply
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO name, please enter your chosen name as text
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="maxNFTSupply"
            rules={[
              { required: true, message: 'Please input the total supply!' },
              {
                type: 'number',
                min: 1,
                message: 'Total supply must be greater than or equal to 1!',
              },
            ]}
          >
            <InputNumber
              style={{ color: 'rgba(0, 0, 0, 0.85)', width: '100%' }}
              min={1}
              disabled={!isModeCreate}
              precision={0}
              placeholder="Enter total supply"
            />
          </Form.Item>
        </Col>

        {/* PTO Type */}
        <Col span={12}>
          <Form.Item
            label={
              <>
                PTO Type
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">Select the type of PTO for this property</p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="ptoType"
            initialValue={PTOTypeEnum.PTO}
            rules={[{ required: true, message: 'Please select the PTO type!' }]}
          >
            <Select disabled={!edit} options={PTO_TYPE_OPTIONS} />
          </Form.Item>
        </Col>

        {/* NFT Price */}
        <Col span={12}>
          <Form.Item
            label={
              <>
                NFT Price
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO NFT price, please enter your chosen price as number
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="pricePerNFT"
            rules={[
              { required: true, message: 'Please input the nft price!' },
              {
                type: 'number',
                min: 0.0000001,
                message: 'NFT price must be greater than or equal to 0.0000001!',
              },
            ]}
          >
            <InputNumber
              style={{
                color: 'rgba(0, 0, 0, 0.85)',
                width: '100%',
              }}
              min={0.0000001}
              placeholder="Enter the nft price!"
              disabled={!isModeCreate}
              addonBefore={'$'}
              addonAfter={'USDC'}
            />
          </Form.Item>
        </Col>

        {/* Country */}
        <Col span={12}>
          <Form.Item
            name="country"
            label={
              <>
                Country
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO country, please select your chosen country
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            required
          >
            <SelectCountry
              disabled={!edit}
              value={form.getFieldValue('country')}
              onChange={(value) => {
                form.setFieldsValue({ country: value })
              }}
            />
          </Form.Item>
        </Col>

        {/* Royalty */}
        <Col style={{ marginBottom: 12 }} span={12}>
          <Form.Item
            label={
              <>
                Royalty
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO royalty, please enter the correct royalty and check its
                        information
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="royalty"
            rules={[
              {
                required: true,
                message: 'Please input the royalty!',
              },
            ]}
            style={{ marginBottom: 12 }}
            // getValueFromEvent={(value) => {
            //   // When user inputs 1, store as 100
            //   return value !== null && value !== undefined ? value * 100 : null;
            // }}
            // getValueProps={(value) => ({
            //   // When displaying 100, show as 1
            //   value: value !== null && value !== undefined ? value / 100 : null,
            // })}
          >
            <InputNumber
              style={{
                color: 'rgba(0, 0, 0, 0.85)',
                width: '100%',
              }}
              min={0}
              max={100}
              placeholder="Enter the royalty"
              disabled={!isModeCreate}
              addonAfter={'%'}
            />
          </Form.Item>
        </Col>

        {/* Contract owner address */}
        <Col style={{ marginBottom: 12 }} span={12}>
          <Form.Item
            label={
              <>
                Contract Owner Address
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO contract owner address, please enter the correct contract
                        owner address and check its information
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="ownerAddress"
            rules={[
              {
                required: true,
                message: 'Please input the contract owner address!',
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()

                  const valid = isAddress(value) // validation bằng viem

                  return valid ? Promise.resolve() : Promise.reject('Invalid blockchain address!')
                },
              },
            ]}
            style={{ marginBottom: 12 }}
          >
            <Input
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              disabled={!isModeCreate}
              placeholder="Enter the contract owner address"
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Row>
            {/* Phí giao dịch */}
            <Col span={24}>
              <Form.Item
                label={
                  <>
                    Transaction Fee
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your PTO transaction fee, please enter the correct transaction
                            fee and check its information
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="transactionFee"
                rules={[
                  {
                    required: true,
                    message: 'Please input the transaction fee!',
                  },
                ]}
                style={{ marginBottom: 12 }}
                // getValueFromEvent={(value) => {
                //   // When user inputs 1, store as 100
                //   return value !== null && value !== undefined
                //     ? value * 100
                //     : null;
                // }}
                // getValueProps={(value) => ({
                //   // When displaying 100, show as 1
                //   value:
                //     value !== null && value !== undefined ? value / 100 : null,
                // })}
              >
                <InputNumber
                  style={{
                    color: 'rgba(0, 0, 0, 0.85)',
                    width: '100%',
                  }}
                  min={0}
                  max={100}
                  placeholder="Enter the transaction fee"
                  disabled={!isModeCreate}
                  addonAfter={'%'}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        {/* Description */}
        <Col span={12}>
          <Row>
            {/* Mô tả */}
            <Col span={24}>
              <Form.Item
                label={
                  <>
                    Contract Address
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your PTO description, please enter your chosen description as
                            text
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="contractAddress"
                hidden={isModeCreate}
              >
                <Input
                  style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                  disabled={!isModeCreate}
                  placeholder="Enter the contract address"
                />
              </Form.Item>
            </Col>
            {/* Mô tả */}
            <Col span={24}>
              <Form.Item
                label={
                  <>
                    Description
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your PTO description, please enter your chosen description as
                            text
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="description"
                rules={[
                  {
                    required: true,
                    message: 'Please input the description!',
                  },
                  {
                    min: 3,
                    max: 500,
                    message: 'Description must be between 3 and 500 characters!',
                  },
                ]}
              >
                <MarkdownEditor disabled={!edit} placeholder="Enter the description" />
              </Form.Item>
            </Col>
            {/* Ngày bán thứ cấp */}
            <Col span={24}>
              <Form.Item
                label={
                  <>
                    Secondary Sale Date
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your PTO description, please enter your chosen description as
                            text
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="tradeUnlockTime"
                getValueFromEvent={(date) => (date ? Math.floor(date.valueOf() / 1000) : null)}
                getValueProps={(value) => ({
                  value: value ? convertNumberToDateTime(value) : null,
                })}
                // rules={[
                //   {
                //     required: true,
                //     message: 'Please input the secondary sale date!',
                //   },
                // ]}
              >
                <DatePicker className="w-full" disabled={!edit} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Row>
            {/* Trạng thái */}
            {!isModeCreate ? (
              <Col span={6}>
                <Form.Item
                  label={
                    <>
                      Status
                      <Tooltip
                        title={
                          <div className="tooltip-box">
                            <p className="tooltip-text">
                              Toggle the switch to manage your PTO status. When the switch is{' '}
                              <strong>ON</strong>, the is active. When the switch is{' '}
                              <strong>OFF</strong>, the is inactive.
                            </p>
                          </div>
                        }
                        placement="right"
                      >
                        <QuestionCircleOutlined className="tooltip-icon" />
                      </Tooltip>
                    </>
                  }
                  name="status"
                  required
                >
                  {isModeCreate ? (
                    <Switch />
                  ) : (
                    <Switch checked={switchChecked} disabled={!edit} onClick={handleSwitchClick} />
                  )}
                </Form.Item>
              </Col>
            ) : null}
            {/* Enable Marketplace */}
            {!isModeCreate ? (
              <MarketPlaceProvider>
                <ButtonEnableMarketPlace form={form} edit={edit} />
              </MarketPlaceProvider>
            ) : null}
            {/* Send Gift */}
            {!isModeCreate ? <ButtonSendGift edit={edit} /> : null}
            {/* Feature */}
            <Col span={6}>
              <Form.Item
                label={
                  <>
                    Feature
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            Toggle the switch to manage your PTO . When the switch is{' '}
                            <strong>ON</strong>, the is active. When the switch is{' '}
                            <strong>OFF</strong>, the is inactive.
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="isFeatured"
              >
                <Switch disabled={!edit} />
              </Form.Item>
            </Col>
            {/* isShowDetail */}
            <Col span={6}>
              <Form.Item
                label={
                  <>
                    Is Show detail
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            Toggle the switch to enable PTO detail view. When the switch is{' '}
                            <strong>ON</strong>, users can click the PTO to view the detail page.{' '}
                            When the switch is <strong>OFF</strong>, the detail page is disabled.{' '}
                            Default value is <strong>ON</strong>.
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="isShowDetail"
              >
                <Switch disabled={!edit} />
              </Form.Item>
            </Col>
            {/* isShowInList */}
            <Col span={6}>
              <Form.Item
                label={
                  <>
                    Is Show In List
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            Toggle the switch to enable PTO to be shown in the list. When the switch
                            is <strong>ON</strong>, the PTO is visible in the list. When the switch
                            is <strong>OFF</strong>, the PTO is hidden from the list. Default value
                            is <strong>ON</strong>.
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="isShowList"
              >
                <Switch disabled={!edit} />
              </Form.Item>
            </Col>
            <Col span={24}>
              {!isModeCreate ? (
                <Form.Item name="whitelist" label="Whitelist">
                  <ButtonUploadWhitelist id={id} edit={edit} />
                </Form.Item>
              ) : null}
            </Col>
          </Row>
        </Col>

        {/* YouTube Link */}
        <Col span={12}>
          <Form.Item
            label={
              <>
                YouTube
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        Enter the YouTube video or channel URL for this PTO
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name={['typeSocial', 'youtube']}
            rules={[{ type: 'url', message: 'Please enter a valid URL!' }]}
          >
            <Input
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              disabled={!edit}
              placeholder="https://youtube.com/..."
            />
          </Form.Item>
        </Col>

        {/* Terms of Use */}
        <Col span={24}>
          <Form.Item
            label={
              <>
                Terms of Use
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO Terms of Use, User need to accept these terms before
                        purchasing or renting the PTO.
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="terms"
          >
            <MarkdownEditor disabled={!edit} placeholder="Enter the Terms of Use" />
          </Form.Item>
        </Col>
      </Row>
    </fieldset>
  )
}

export default BaseInformation
